import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Grid,
    useTheme,
    useMediaQuery,
    Paper,
    CircularProgress,
    IconButton
} from '@mui/material';
import {
    IconPhone,
    IconBrandWhatsapp,
    IconCircleCheck,
    IconUsers,
    IconCrown,
    IconCreditCard,
    IconBolt,
    IconInfoCircle
} from '@tabler/icons-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { colors, fonts } from '../../ColorFont/color';
import { url, rzkey } from '../../ConstUrl/Url';
import { loadRazorpay } from '../../utils/loadRazorpay';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useUser } from '../../context/UserContext';

const BuyerBoost = () => {
    const { userData, setUserData } = useUser();
    const { t } = useTranslation(['dashboard', 'common']);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();
    const location = useLocation();

    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [processingPayment, setProcessingPayment] = useState(false);

    useEffect(() => {
        fetchPlans();
    }, []);

    const fetchPlans = async () => {
        try {
            const response = await fetch(`${url}/api/plans`);
            const data = await response.json();
            // Data structure from backend: { _id, name, price, contacts, duration }
            setPlans(data);
            if (data.length > 0) {
                // Find most popular or default to second one
                setSelectedPlan(data[1]?._id || data[0]?._id);
            }
        } catch (error) {
            console.error("Error fetching plans:", error);
            toast.error(t('common:failedToLoadPlans'));
        } finally {
            setLoading(false);
        }
    };

    const handlePayment = async () => {
        if (!selectedPlan) return;
        const plan = plans.find(p => p._id === selectedPlan);
        
        setProcessingPayment(true);
        try {
            const planPrice = plan.discountedPrice || plan.price;
            // 1. Create Order on Backend
            const orderRes = await axios.post(`${url}/create-order`, {
                amount: planPrice,
                currency: "INR"
            });

            const { id: order_id, amount, currency } = orderRes.data;

        // 2. Load Razorpay on-demand and open checkout
            const razorpayLoaded = await loadRazorpay();
            if (!razorpayLoaded) {
                toast.error(t('common:failedToLoadPaymentGateway'));
                return;
            }

            // 3. Open Razorpay Checkout
            const options = {
                key: rzkey,
                amount: amount,
                currency: currency,
                name: "KrushiSetu",
                description: `${t('common:purchasePrefix')}${plan.name} - ${plan.contacts} ${t('common:contactsLabel').replace(':', '').trim()}`,
                order_id: order_id,
                handler: async (response) => {
                    // 3. Verify Payment
                    try {
                        const verifyRes = await axios.post(`${url}/verify-payment`, {
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_signature: response.razorpay_signature
                        });

                        if (verifyRes.data.success) {
                            // 4. Update Plan on User account
                            const buyRes = await axios.post(`${url}/api/buy-plan`, {
                                userId: userData._id,
                                planId: plan._id
                            });

                            if (buyRes.status === 200) {
                                toast.success(t('common:planActivatedSuccess'));
                                // Update user context (updates navbar and UI immediately)
                                setUserData(buyRes.data.user);
                                localStorage.setItem('userData', JSON.stringify(buyRes.data.user));
                                // Redirect to home without hard reload
                                navigate('/home');
                            }
                        }
                    } catch (err) {
                        console.error("Verification failed:", err);
                        toast.error(t('common:paymentProcessFailed'));
                    }
                },
                prefill: {
                    name: userData?.name || "",
                    email: userData?.email || "",
                    contact: userData?.mobile || ""
                },
                theme: {
                    color: colors.primary
                }
            };

            const rzp = new window.Razorpay(options);
            rzp.on('payment.failed', function (response){
                toast.error(t('common:paymentFailedPrefix') + response.error.description);
            });
            rzp.open();

        } catch (err) {
            console.error("Payment error:", err);
            toast.error(t('common:failedToInitiatePayment'));
        } finally {
            setProcessingPayment(false);
        }
    };

    const benefits = [
        { text: t("dashboard:benefitDirectPhone"), icon: <IconPhone size={18} /> },
        { text: t("dashboard:benefitWhatsApp"), icon: <IconBrandWhatsapp size={18} /> },
        { text: t("dashboard:benefitSaveContacts"), icon: <IconCircleCheck size={18} /> },
        { text: t("dashboard:benefitNoHidden"), icon: <IconCircleCheck size={18} /> }
    ];

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                <CircularProgress color="primary" />
            </Box>
        );
    }

    return (
        <Box sx={{
            padding: 0,
            maxWidth: '1600px',
            margin: '0 auto',
            minHeight: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '32px',
            backgroundColor: '#FFFFFF'
        }}>
            {/* Page Header */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                <Box sx={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '50%',
                    backgroundColor: colors.primary_rgba_8,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: colors.primary,
                    mb: '8px'
                }}>
                    <IconPhone size={32} stroke={1.5} />
                </Box>
                <Typography sx={{
                    fontFamily: fonts.manrope || fonts.poppins,
                    fontWeight: 800,
                    fontSize: isMobile ? '24px' : '30px',
                    lineHeight: '36px',
                    color: '#0F1729',
                    textAlign: 'center'
                }}>
                    {t('dashboard:unlockFarmerHeader')}
                </Typography>
                <Typography sx={{
                    fontFamily: fonts.poppins,
                    fontWeight: 400,
                    fontSize: '15px',
                    color: '#637381',
                    textAlign: 'center'
                }}>
                    {t('dashboard:unlockFarmerSub')}
                </Typography>
            </Box>

            {/* Current Plan Banner */}
            <Box sx={{
                width: '100%',
                background: 'linear-gradient(135deg, #00A76F 0%, #007867 100%)',
                borderRadius: '24px',
                padding: isMobile ? '28px 20px' : '40px 48px',
                color: '#FFFFFF',
                position: 'relative',
                boxShadow: '0px 12px 24px -4px rgba(0, 144, 99, 0.16)'
            }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: '32px' }}>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: '8px' }}>
                            <Typography sx={{ fontWeight: 700, fontSize: '16px', opacity: 0.95, fontFamily: fonts.poppins }}>{t('dashboard:currentPlan') || "Current Plan"}</Typography>
                            <IconButton 
                                size="small" 
                                onClick={() => navigate('/purchase-history')}
                                sx={{ color: '#FFFFFF', padding: 0, opacity: 0.8, '&:hover': { opacity: 1 } }}
                            >
                                <IconInfoCircle size={18} />
                            </IconButton>
                        </Box>
                        <Typography sx={{ fontWeight: 800, fontSize: isMobile ? '36px' : '48px', lineHeight: 1, fontFamily: fonts.poppins }}>
                            {userData?.noOfContacts > 0 ? t('dashboard:activePlan') : t('dashboard:noActivePlan')}
                        </Typography>
                    </Box>
                    <Box sx={{
                        backgroundColor: '#FFFFFF',
                        color: userData?.noOfContacts > 0 ? colors.primary : '#637381',
                        padding: '8px 18px',
                        borderRadius: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)'
                    }}>
                        <Box sx={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: userData?.noOfContacts > 0 ? colors.primary : '#637381' }} />
                        <Typography sx={{ fontSize: '13px', fontWeight: 700, fontFamily: fonts.poppins }}>
                            {userData?.noOfContacts > 0 ? t('common:active') : t('common:inactive')}
                        </Typography>
                    </Box>
                </Box>

                <Grid container spacing={4}>
                    <Grid item xs={6} sm={4}>
                        <Typography sx={{ fontSize: '12px', fontWeight: 600, opacity: 0.8, mb: '4px', textTransform: 'uppercase', fontFamily: fonts.poppins }}>{t('dashboard:availableContacts')}</Typography>
                        <Typography sx={{ fontSize: '22px', fontWeight: 800, fontFamily: fonts.poppins }}>{userData?.noOfContacts || 0} {t('common:contactsLabel').replace(':', '').trim()}</Typography>
                    </Grid>
                </Grid>
            </Box>

            {/* What You Get Card */}
            <Box sx={{
                backgroundColor: '#FFFFFF',
                borderRadius: '24px',
                padding: '32px',
                border: `1px solid ${colors.border || '#E3E8E3'}`,
                boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.04)'
            }}>
                <Typography sx={{
                    fontFamily: fonts.manrope || fonts.poppins,
                    fontWeight: 700,
                    fontSize: '18px',
                    color: '#171C17',
                    mb: '24px'
                }}>
                    {t('dashboard:whatYouGet')}
                </Typography>
                <Grid container spacing={2.5}>
                    {benefits.map((benefit, idx) => (
                        <Grid item xs={12} sm={6} key={idx}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <Box sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    color: colors.primary 
                                }}>
                                    {benefit.icon}
                                </Box>
                                <Typography sx={{
                                    fontFamily: fonts.poppins,
                                    fontSize: '15px',
                                    fontWeight: 500,
                                    color: '#6D786D'
                                }}>
                                    {benefit.text}
                                </Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* Plan selection */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px', mb: '40px' }}>
                <Typography sx={{
                    fontFamily: fonts.poppins,
                    fontWeight: 700,
                    fontSize: '18px',
                    color: '#171C17'
                }}>
                    {t('dashboard:chooseYourPack')}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {plans.map((plan, index) => (
                        <Box
                            key={plan._id}
                            onClick={() => setSelectedPlan(plan._id)}
                            sx={{
                                padding: '24px',
                                borderRadius: '24px',
                                border: `2px solid ${selectedPlan === plan._id ? colors.primary : colors.border || '#E3E8E3'}`,
                                backgroundColor: selectedPlan === plan._id ? colors.primary_rgba_4 : '#FFFFFF',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '16px',
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                position: 'relative',
                                '&:hover': {
                                    borderColor: colors.primary,
                                    backgroundColor: colors.primary_rgba_4
                                }
                            }}
                        >
                            {plan.isMostPopular && (
                                <Box sx={{
                                    position: 'absolute',
                                    top: '-12px',
                                    left: '24px',
                                    background: '#B71D18',
                                    color: '#FFF',
                                    px: 1.5,
                                    py: 0.5,
                                    borderRadius: '50px',
                                    fontSize: '11px',
                                    fontWeight: 800,
                                    fontFamily: fonts.poppins,
                                    letterSpacing: '0.5px',
                                    textTransform: 'uppercase'
                                }}>
                                    {t('dashboard:mostPopular')}
                                </Box>
                            )}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                                <Box sx={{
                                    width: '56px',
                                    height: '56px',
                                    borderRadius: '24px',
                                    backgroundColor: selectedPlan === plan._id ? colors.primary_rgba_12 : '#F4F6F8',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: selectedPlan === plan._id ? colors.primary : '#637381'
                                }}>
                                    <IconUsers size={28} />
                                </Box>
                                <Box>
                                    <Typography sx={{ fontWeight: 800, fontSize: '18px', color: '#171C17', fontFamily: fonts.poppins }}>{plan.name}</Typography>
                                    <Typography sx={{ fontSize: '14px', color: '#637381', fontFamily: fonts.poppins }}>{plan.contacts} {t('common:contactsLabel').replace(':', '').trim()}</Typography>
                                </Box>
                            </Box>
                            <Box sx={{ textAlign: 'right' }}>
                                <Typography sx={{ fontWeight: 800, fontSize: '24px', color: colors.primary, fontFamily: fonts.poppins }}>₹{plan.discountedPrice || plan.price}</Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* Bottom Actions */}
            <Box sx={{ 
                display: 'flex', 
                justifyContent: isMobile ? 'column' : 'flex-end', 
                gap: 2, 
                pb: 10,
                marginTop: 'auto'
            }}>
                <Button
                    onClick={() => navigate(-1)}
                    sx={{
                        height: '56px',
                        px: 4,
                        borderRadius: '24px',
                        fontWeight: 700,
                        fontFamily: fonts.poppins,
                        color: '#637381',
                        border: '1px solid #919EAB33',
                        flex: isMobile ? 1 : 'none'
                    }}
                >
                    {t('common:cancel')}
                </Button>
                <Button
                    variant="contained"
                    onClick={handlePayment}
                    disabled={!selectedPlan || processingPayment}
                    startIcon={processingPayment ? <CircularProgress size={20} color="inherit" /> : <IconBolt size={20} fill="#FFF" />}
                    sx={{
                        height: '56px',
                        px: 6,
                        borderRadius: '24px',
                        fontWeight: 700,
                        fontFamily: fonts.poppins,
                        backgroundColor: colors.primary,
                        backgroundImage: colors.button,
                        boxShadow: '0px 8px 16px colors.primary_rgba_24',
                        flex: isMobile ? 1 : 'none',
                        '&:hover': {
                            backgroundColor: colors.primary_dark
                        }
                    }}
                >
                    {t('common:purchase')}
                </Button>
            </Box>
        </Box>
    );
};

export default BuyerBoost;
